import { defineStore } from 'pinia'
import { get, set } from 'idb-keyval'
import { deleteImage } from '~/utils/imageStore'

export enum InventoryStatus {
    DRAFT = 'draft',
    SUBMITTED = 'submitted',
    COMPLETED = 'completed'
}

export interface InventoryItem {
    id: string
    name: string
    value: number
    photos: string[]
}

export interface InventoryRoom {
    id: string
    name: string
    items: InventoryItem[]
}

export interface InventoryReport {
    id: string
    name: string
    createdAt: string
    completedOn?: string
    customerSessionToken?: string
    checkoutId?: string
    status: InventoryStatus
    rooms: InventoryRoom[]
}

export interface InventoryState {
    reports: InventoryReport[]
    isLoading: boolean
}

const DEFAULT_ROOMS = ['Living Room', 'Kitchen', 'Bedroom']

async function deletePhotos(photoUrls: string[]) {
    await Promise.allSettled(
        photoUrls.filter(url => url.startsWith('local:')).map(deleteImage)
    )
}

export const useInventoryStore = defineStore('inventory', () => {
    const state = reactive<InventoryState>({
        reports: [],
        isLoading: true
    })

    const isLoading = computed(() => state.isLoading)
    const reports = computed(() => state.reports)

    const saveToDB = async () => {
        await set('claim-ready-reports', JSON.parse(JSON.stringify(state.reports)))
    }

    const init = async () => {
        // Try to load from IndexedDB first
        const savedReports: any[] | undefined = await get('claim-ready-reports')
        if (savedReports) {
            // Migration: Handle legacy reports with flat 'items' array
            state.reports = savedReports.map((r: any) => {
                let rooms = r.rooms || []

                // Ensure at least one room exists if empty
                if (rooms.length === 0) {
                    rooms = DEFAULT_ROOMS.map(name => ({
                        id: globalThis.crypto.randomUUID(),
                        name,
                        items: []
                    }))
                }

                return {
                    id: r.id,
                    name: r.name || `Report - ${new Date(r.createdAt || Date.now()).toLocaleDateString()}`,
                    createdAt: r.createdAt,
                    completedOn: r.completedOn,
                    customerSessionToken: r.customerSessionToken,
                    checkoutId: r.checkoutId,
                    status: r.status || InventoryStatus.DRAFT,
                    rooms
                }
            })
        }

        state.isLoading = false
    }

    const createReport = async () => {
        const dateStr = new Date().toLocaleDateString()
        const newReport: InventoryReport = {
            id: globalThis.crypto.randomUUID(),
            name: `Report - ${dateStr}`,
            createdAt: new Date().toISOString(),
            customerSessionToken: '',
            checkoutId: '',
            status: InventoryStatus.DRAFT,
            rooms: DEFAULT_ROOMS.map(name => ({
                id: globalThis.crypto.randomUUID(),
                name,
                items: []
            }))
        }
        state.reports.unshift(newReport) // Add to top
        await saveToDB()
        return newReport.id
    }

    const getReport = (id: string) => {
        return state.reports.find(r => r.id === id)
    }

    const updateReport = async (id: string, data: Partial<InventoryReport>) => {
        const report = state.reports.find(r => r.id === id)
        if (report) {
            Object.assign(report, data)
            await saveToDB()
        }
    }

    const deleteReport = async (id: string) => {
        const report = state.reports.find(r => r.id === id)
        if (report) {
            // Delete all photos from all items in this report
            const deletePromises: Promise<void>[] = []
            for (const room of report.rooms) {
                for (const item of room.items) {
                    if (item.photos.length) {
                        deletePromises.push(deletePhotos(item.photos))
                    }
                }
            }

            // Allow delete to proceed in background, but log errors if any
            Promise.allSettled(deletePromises)

            state.reports = state.reports.filter(r => r.id !== id)
            await saveToDB()
        }
    }

    const addRoom = async (reportId: string, name: string = 'New Room') => {
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            report.rooms.push({
                id: globalThis.crypto.randomUUID(),
                name,
                items: []
            })
            await saveToDB()
        }
    }

    const updateRoom = async (reportId: string, roomId: string, name: string) => {
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            const room = report.rooms.find(r => r.id === roomId)
            if (room) {
                room.name = name
                await saveToDB()
            }
        }
    }

    const removeRoom = async (reportId: string, roomId: string) => {
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            const roomIndex = report.rooms.findIndex(r => r.id === roomId)
            const room = report.rooms[roomIndex]

            if (room && roomIndex !== -1) {
                // 1. Parallel Delete Photos for all items
                const deletePromises: Promise<void | void>[] = []

                for (const item of room.items) {
                    if (item.photos && item.photos.length > 0) {
                        deletePromises.push(deletePhotos(item.photos))
                    }
                }

                await Promise.allSettled(deletePromises)

                // 2. Remove Room
                report.rooms.splice(roomIndex, 1)
                await saveToDB()
            }
        }
    }

    const addItemToRoom = async (reportId: string, roomId: string, item: Omit<InventoryItem, 'id' | 'photos'>) => {
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            const room = report.rooms.find(r => r.id === roomId)
            if (room) {
                const newItem: InventoryItem = {
                    ...item,
                    id: globalThis.crypto.randomUUID(),
                    photos: []
                }
                room.items.push(newItem)
                await saveToDB()
                return newItem
            }
        }
    }

    const updateItem = async (reportId: string, itemId: string, itemData: Partial<InventoryItem>) => {
        // Search all rooms for the item
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            for (const room of report.rooms) {
                const itemIndex = room.items.findIndex(i => i.id === itemId)
                if (itemIndex !== -1) {
                    const oldItem = room.items[itemIndex]

                    if (!oldItem) return;

                    // Check for removed photos
                    if (itemData.photos) {
                        // Extract keys for diffing to handle signed URL expiration/rotation
                        const getBaseKey = (url: string) => {
                            try {
                                // Handle full URL with query params
                                const path = new URL(url).pathname
                                // Decode to ensure we match against encoded/unencoded variations consistent with S3 keys
                                return decodeURIComponent(path)
                            } catch (e) {
                                return decodeURIComponent(url.split('?')[0] as string)
                            }
                        }

                        const newKeys = new Set(itemData.photos!.map(getBaseKey))
                        const removedPhotos = oldItem.photos.filter(p => !newKeys.has(getBaseKey(p)))

                        if (removedPhotos.length) {
                            await deletePhotos(removedPhotos)
                        }
                    }

                    room.items[itemIndex] = { ...oldItem, ...itemData } as InventoryItem
                    await saveToDB()
                    return
                }
            }
        }
    }

    const removeItem = async (reportId: string, itemId: string) => {
        // Search all rooms for the item
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            for (const room of report.rooms) {
                const initialCount = room.items.length
                const item = room.items.find(i => i.id === itemId)

                if (item) {
                    // Delete photos
                    if (item.photos.length) {
                        // Intentionally not awaiting to speed up UI, but errors might be swallowed
                        deletePhotos(item.photos)
                    }
                }

                room.items = room.items.filter(i => i.id !== itemId)
                if (room.items.length !== initialCount) {
                    await saveToDB()
                    return
                }
            }
        }
    }

    const cleanupEmptyItems = async (reportId: string) => {
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            let changed = false
            const originalRoomCount = report.rooms.length

            // 1. Filter out empty items in each room
            for (const room of report.rooms) {
                const initialCount = room.items.length
                // Filter out items with no name AND no value (or value 0)
                const validItems = room.items.filter(i => {
                    const hasName = i.name && i.name.trim().length > 0;
                    const hasValue = i.value && Number(i.value) > 0;
                    const hasPhotos = i.photos && i.photos.length > 0; // Keep if it has photos? Usually user wants to keep.

                    // Keep if it has ANY content
                    return hasName || hasValue || hasPhotos;
                });

                if (validItems.length !== initialCount) {
                    room.items = validItems
                    changed = true
                }
            }

            // 2. Filter out empty rooms (rooms with 0 items)
            const nonEmptyRooms = report.rooms.filter(r => r.items.length > 0)
            if (nonEmptyRooms.length !== originalRoomCount) {
                report.rooms = nonEmptyRooms
                changed = true
            }

            if (changed) {
                await saveToDB()
            }
        }
    }

    const markReportAsCompleted = async (reportId: string) => {
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            report.status = InventoryStatus.COMPLETED
            report.completedOn = new Date().toISOString()
            await saveToDB()
        }
    }

    const updateWithPaymentData = async (reportId: string, checkoutId: string, customerSessionToken: string) => {
        console.log('Updating report with payment data:', { reportId, checkoutId })
        const report = state.reports.find(r => r.id === reportId)
        if (report) {
            report.customerSessionToken = customerSessionToken
            report.checkoutId = checkoutId
            console.log('Report updated locally:', report)
            await saveToDB()
        } else {
            console.error('Report not found for payment update:', reportId)
        }
    }

    return {
        reports,
        isLoading,
        init,
        createReport,
        getReport,
        updateReport,
        deleteReport,
        addRoom,
        removeRoom,
        updateRoom,
        addItemToRoom,
        updateItem,
        removeItem,
        cleanupEmptyItems,
        markReportAsCompleted,
        updateWithPaymentData
    }
})